import { useState, useEffect } from "react";
import { textBlocksAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Edit, Trash2, FileText, TextCursor, Scale, FileWarning, ClipboardList, Briefcase, Gavel, ScrollText, ShieldCheck, Power, ToggleLeft, ToggleRight } from "lucide-react";

const blockTypes = [
  { id: "intro", name: "Einleitung", icon: TextCursor, description: "Begrüßungstext im Angebot" },
  { id: "outro", name: "Abschluss", icon: FileText, description: "Schlusstext im Angebot" },
  { id: "agb", name: "AGB", icon: Scale, description: "Allgemeine Geschäftsbedingungen" },
  { id: "widerrufsbelehrung", name: "Widerrufsbelehrung", icon: FileWarning, description: "Rechtliche Widerrufsbelehrung" },
  { id: "widerrufsformular", name: "Widerrufsformular", icon: ClipboardList, description: "Formular für Widerruf" },
  { id: "notes", name: "Hinweise", icon: FileText, description: "Zusätzliche Hinweise" },
  // Kaufvertrag-Textbausteine
  { id: "montage_conditions", name: "Montagebedingungen", icon: Briefcase, description: "Bedingungen für die Montage" },
  { id: "seller_duties", name: "Pflichten Verkäufer", icon: ShieldCheck, description: "Pflichten des Verkäufers" },
  { id: "buyer_duties", name: "Pflichten Käufer", icon: ScrollText, description: "Pflichten des Käufers" },
  { id: "legal_clause", name: "Rechtliche Klauseln", icon: Gavel, description: "Rechtliche Bestimmungen" },
  { id: "additional_terms", name: "Zusätzliche Vereinbarungen", icon: FileText, description: "Weitere Vereinbarungen" }
];

export default function TextBlocksPage() {
  const { user } = useAuth();
  const [textBlocks, setTextBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("intro");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [formData, setFormData] = useState({
    type: "intro",
    name: "",
    content: "",
    active: true,
    tier: "both"  // "standard", "secure_plus", or "both"
  });

  const isAdmin = user?.role === "superadmin" || user?.role === "admin";

  useEffect(() => {
    loadTextBlocks();
  }, []);

  const loadTextBlocks = async () => {
    try {
      const response = await textBlocksAPI.list();
      setTextBlocks(response.data);
    } catch (error) {
      console.error("Failed to load text blocks:", error);
      toast.error("Fehler beim Laden der Textbausteine");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        type: activeTab
      };

      if (editingBlock) {
        await textBlocksAPI.update(editingBlock.id, data);
        toast.success("Textbaustein aktualisiert");
      } else {
        await textBlocksAPI.create(data);
        toast.success("Textbaustein erstellt");
      }
      
      setDialogOpen(false);
      resetForm();
      loadTextBlocks();
    } catch (error) {
      console.error("Failed to save text block:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diesen Textbaustein wirklich löschen?")) return;
    
    try {
      await textBlocksAPI.delete(id);
      toast.success("Textbaustein gelöscht");
      loadTextBlocks();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleActive = async (block) => {
    try {
      await textBlocksAPI.toggleActive(block.id);
      toast.success(block.active ? "Textbaustein deaktiviert" : "Textbaustein aktiviert");
      loadTextBlocks();
    } catch (error) {
      console.error("Failed to toggle active:", error);
      toast.error("Fehler beim Ändern des Status");
    }
  };

  const handleEdit = (block) => {
    setEditingBlock(block);
    setFormData({
      type: block.type,
      name: block.name,
      content: block.content,
      active: block.active,
      tier: block.tier || "both"
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBlock(null);
    setFormData({
      type: activeTab,
      name: "",
      content: "",
      active: true,
      tier: "both"
    });
  };

  const filteredBlocks = textBlocks.filter(b => b.type === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="textblocks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Textbausteine</h1>
          <p className="text-muted-foreground">
            Vordefinierte Texte für Angebote
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-textblock-btn">
            <Plus className="h-4 w-4 mr-2" />
            Neuer Textbaustein
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {blockTypes.map((type) => (
            <TabsTrigger key={type.id} value={type.id}>
              {type.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {blockTypes.map((type) => (
          <TabsContent key={type.id} value={type.id} className="mt-6">
            {filteredBlocks.length > 0 ? (
              <div className="space-y-4">
                {filteredBlocks.map((block) => (
                  <Card 
                    key={block.id} 
                    className={`card-hover transition-all duration-200 ${!block.active ? 'opacity-50 bg-muted/30' : ''}`}
                    data-testid={`textblock-card-${block.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className={!block.active ? 'opacity-70' : ''}>
                          <CardTitle className="text-lg">{block.name}</CardTitle>
                          <div className="flex gap-2 mt-1">
                            <Badge variant={block.active ? "default" : "secondary"}>
                              {block.active ? "Aktiv" : "Inaktiv"}
                            </Badge>
                            {block.tier === "standard" && (
                              <Badge variant="outline" className="border-orange-500 text-orange-600">Standard</Badge>
                            )}
                            {block.tier === "secure_plus" && (
                              <Badge variant="outline" className="border-blue-500 text-blue-600">Secure Plus</Badge>
                            )}
                            {block.tier === "both" && (
                              <Badge variant="outline" className="border-gray-400 text-gray-600">Beide</Badge>
                            )}
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-2 items-center">
                            <Button
                              variant={block.active ? "ghost" : "outline"}
                              size="sm"
                              onClick={() => handleToggleActive(block)}
                              className={`transition-colors ${block.active ? 'text-green-600 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                              title={block.active ? "Deaktivieren" : "Aktivieren"}
                              data-testid={`toggle-active-${block.id}`}
                            >
                              {block.active ? (
                                <ToggleRight className="h-5 w-5" />
                              ) : (
                                <ToggleLeft className="h-5 w-5" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(block)}
                              data-testid={`edit-textblock-${block.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(block.id)}
                              className="text-destructive"
                              data-testid={`delete-textblock-${block.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className={!block.active ? 'opacity-70' : ''}>
                      <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                        {block.content}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <TextCursor className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">Keine {type.name}-Texte vorhanden</h3>
                  <p className="text-muted-foreground mb-4">
                    Erstellen Sie Ihren ersten Textbaustein
                  </p>
                  {isAdmin && (
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Textbaustein hinzufügen
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Text Block Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingBlock ? "Textbaustein bearbeiten" : "Neuer Textbaustein"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {blockTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Standard Einleitung"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Inhalt *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Geben Sie hier den Text ein..."
                rows={8}
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Aktiv</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label>Anlagenklasse</Label>
              <Select
                value={formData.tier}
                onValueChange={(value) => setFormData({ ...formData, tier: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">
                    <span className="text-gray-600">Beide Anlagenklassen</span>
                  </SelectItem>
                  <SelectItem value="standard">
                    <span className="text-orange-600">Nur Standard</span>
                  </SelectItem>
                  <SelectItem value="secure_plus">
                    <span className="text-blue-600">Nur Secure Plus</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Wählen Sie, für welche Anlagenklasse dieser Text verwendet werden soll
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit">
                {editingBlock ? "Speichern" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
